import { useEffect, useId, useRef, useState } from "react";

// BRK-S326 — the "?" beside a bond-details field's label, opening its
// definition (Daniel's feedback #7). The text is the CONFIGURATION's
// (`helpText`, published by discovery and relayed by PMS); a field without it
// renders no button at all.
//
// A click-or-keyboard disclosure rather than a hover tooltip: a phone has no
// hover, and a definition a broker cannot reach by keyboard is not there for
// them. The native button takes Enter and Space; Escape closes and returns
// focus to the button; a click outside closes it.
//
// LAYOUT: the button sits at the end of the label row and the bubble spans the
// FIELD's width beneath it (its positioned ancestor is the field wrapper in
// `BondFieldRenderer`), so it can never run off a 375px screen.

export function FieldDefinition({
  label,
  text,
}: {
  label: string;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        button.current?.contains(target) ||
        bubble.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={`What is “${label}”?`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="absolute right-0 top-0 inline-flex h-5 w-5 items-center justify-center rounded-full border border-brk-border bg-brk-panel text-xs font-semibold text-brk-dim transition-colors hover:text-brk-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brk-accent/40"
      >
        ?
      </button>
      {open && (
        <div
          ref={bubble}
          id={id}
          role="note"
          aria-label={`${label}: definition`}
          className="absolute inset-x-0 top-6 z-20 break-words rounded-md border border-brk-border bg-brk-panel p-3 text-sm text-brk-text shadow-lg"
        >
          {text}
        </div>
      )}
    </>
  );
}
